/**
 * useSystemLookup + 按当前语言替换 label（取字典的 labelI18n，需 includeExtra 才拉回）。
 *
 * 下拉 / 筛选直接用 useSystemLookup 的 label 永远是英文——task_source_type 就是这样：
 * 任务列表、任务日历、任务详情「报告来源」、新建任务、批量建任务五处中文界面都显示 Field Staff
 * （2026-09-26 第三轮 review）。字典本身带 label_i18n，只是没人取。
 *
 * ⚠️ 仅限 lookup-table 字典（后端 lookup.* 表，api db/seeds/lookup/*.json 那 39 个）。includeExtra 走
 * GET /system/lookup-table/tables/{code}?include_extra=true，不是 lookup-table 的字典（如
 * task_resolution_reason_type）在这个端点 404 NOT_FOUND_LOOKUPTABLE，下拉会直接变空 ——
 * 那类字典继续用 useSystemLookup（不带 includeExtra）或它自己的 hook（useTaskResolutionReasons）。
 */
import { useMemo } from 'react';
import { useSystemLookup } from '@/hooks/useLookup';
import { useLocale } from '@/hooks/useLocale';
import { localizeLookupOptions } from '@/utils/localizeLookupOptions';

// 返回类型与 useSystemLookup 一致（data 仍是宽松的 any[]）：调用方从 useSystemLookup 换过来零改动，
// 选项上的其它字段（value / extraInfo …）照常可取。
export function useLocalizedSystemLookup(
  dictionaryName: string,
  opts?: { enabled?: boolean; includeDisabled?: boolean },
): ReturnType<typeof useSystemLookup> {
  const result = useSystemLookup(dictionaryName, { ...opts, includeExtra: true });
  const locale = useLocale();
  const data = useMemo(
    () => (result.data ? localizeLookupOptions(result.data, locale) : result.data),
    [result.data, locale],
  );
  return { ...result, data };
}
